import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n';

// Supported languages – can be easily extended
const SUPPORTED_LANGUAGES = ['hr', 'en', 'sk', 'sv', 'cs'];

// Numeric fields that are shared across all languages
const SHARED_FIELDS = [
    'energy_value',
    'energy_value_kcal',
    'fat',
    'saturated_fat',
    'carbohydrates',
    'sugar',
    'protein',
    'salt',
];

// Initial empty form data for one language
const emptyFormData = {
    name: '',
    allergens: '',
    energy_value: '',
    energy_value_kcal: '',
    fat: '',
    saturated_fat: '',
    carbohydrates: '',
    sugar: '',
    protein: '',
    salt: '',
    category: '',
    alcoholic_strength: '',
    provenance: '',
    producer_info: '',
    net_content: '',
    sugar_content: '',
    durability_date: '',
    ingredients: '',
    nutritional_info: '',
    company_name: '',
    company_address: '',
    company_oib: '',
    company_phone: '',
    company_email: '',
    sulfites: '',
    lang: '',          // will be set per language
    do_not_show: false,
};

const MultiLanguageForm = ({ initialData, onSave, isLoading }) => {
    const { t } = useTranslation();

    // Build initial state for all supported languages
    const buildInitialState = () => {
        const state = {};
        SUPPORTED_LANGUAGES.forEach(lang => {
            state[lang] = {
                ...emptyFormData,
                lang: lang,
                ...(initialData?.[lang] || {}),
            };
        });
        return state;
    };

    const [formState, setFormState] = useState(buildInitialState);
    const [currentLang, setCurrentLang] = useState(SUPPORTED_LANGUAGES[0]);

    // Helper: update a field for the current language
    const handleFieldChange = (field, value) => {
        setFormState(prev => ({
            ...prev,
            [currentLang]: {
                ...prev[currentLang],
                [field]: value,
            },
        }));
    };

    // For shared (numeric) fields, update all languages
    const handleSharedFieldChange = (field, value) => {
        setFormState(prev => {
            const updated = { ...prev };
            SUPPORTED_LANGUAGES.forEach(lang => {
                updated[lang] = {
                    ...updated[lang],
                    [field]: value,
                };
            });
            return updated;
        });
    };

    // Generic onChange that decides if the field is shared
    const onChange = e => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;

        if (SHARED_FIELDS.includes(name)) {
            handleSharedFieldChange(name, val);
        } else {
            handleFieldChange(name, val);
        }
    };

    // Build the translation function for the current language
    const getT = () => {
        try {
            return i18n.getFixedT(currentLang);
        } catch {
            return t; // fallback to default
        }
    };
    const tc = getT();

    const handleSave = () => {
        // Convert state object to array (one per language)
        const payload = SUPPORTED_LANGUAGES.map(lang => formState[lang]);
        onSave(payload);
    };

    return (
        <div className="container">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                <h5>{initialData ? t('edit') : t('add_new_product')}:</h5>
                <select value={currentLang} onChange={e => setCurrentLang(e.target.value)} style={{ marginRight: "35px" }}>
                    {SUPPORTED_LANGUAGES.map(lang => (
                        <option key={lang} value={lang}>{lang.toUpperCase()}</option>
                    ))}
                </select>
            </div>

            {isLoading ? (
                <div className="spinner-border text-primary mt-4" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            ) : (
                <form>
                    {/* Do not show checkbox */}
                    <div className="row mb-3">
                        <div style={{ textAlign: 'left', display: 'flex', alignItems: 'center' }}>
                            <label className="side_pull_checkbox" style={{ display: 'flex', alignItems: 'center' }}>
                                <div className="checkbox">
                                    <input
                                        id="do_not_show"
                                        name="do_not_show"
                                        type="checkbox"
                                        checked={formState[currentLang].do_not_show}
                                        onChange={onChange}
                                    />
                                    <label
                                        className="tgl-btn"
                                        htmlFor="do_not_show"
                                        style={{ marginLeft: '10px' }}
                                    >
                                        {t('not_in_use_all_caps')}
                                    </label>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Product name */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className="form-group">
                                <label htmlFor="name">{tc('product_name_uppercase')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="name"
                                    name="name"
                                    onChange={onChange}
                                    value={formState[currentLang].name}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Ingredients + details */}
                    <div className="row mb-3">
                        <div className="col-md-8" style={{ border: '1px solid #ccc', borderRadius: '10px' }}>
                            <div className="form-group">
                                <label htmlFor="ingredients">{tc('composition_uppercase')}</label>
                                <textarea
                                    className="form-control"
                                    id="ingredients"
                                    name="ingredients"
                                    onChange={onChange}
                                    value={formState[currentLang].ingredients}
                                    rows="5"
                                />
                            </div>
                        </div>
                        <div className="col-md-4" style={{ border: '1px solid #ccc', borderRadius: '10px' }}>
                            <div className="form-group">
                                <label>{tc('details_uppercase')}</label>
                                <input
                                    type="text"
                                    className="form-control mb-2"
                                    id="provenance"
                                    name="provenance"
                                    onChange={onChange}
                                    value={formState[currentLang].provenance}
                                    placeholder={tc('name')}
                                />
                                <input
                                    type="text"
                                    className="form-control mb-2"
                                    id="category"
                                    name="category"
                                    onChange={onChange}
                                    value={formState[currentLang].category}
                                    placeholder={tc('category')}
                                />
                                <input
                                    type="text"
                                    className="form-control"
                                    id="alcoholic_strength"
                                    name="alcoholic_strength"
                                    onChange={onChange}
                                    value={formState[currentLang].alcoholic_strength}
                                    placeholder={tc('origin')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Nutritional values + allergens */}
                    <div className="row mb-3">
                        <div className="col-md-8" style={{ border: '1px solid #ccc', borderRadius: '10px' }}>
                            <div className="form-group">
                                <label>{tc('PHV_UPPERCASE')}</label>
                                <div className="row">
                                    <div className="col-6">
                                        <label style={{ fontSize: '13px' }} htmlFor="energy_value">{tc('energy_value_kj')}</label>
                                        <input type="text" className="form-control mb-2" id="energy_value" name="energy_value" onChange={onChange} value={formState[currentLang].energy_value} />
                                    </div>
                                    <div className="col-6">
                                        <label style={{ fontSize: '13px' }} htmlFor="energy_value_kcal">{tc('energy_value_kcal')}</label>
                                        <input type="text" className="form-control mb-2" id="energy_value_kcal" name="energy_value_kcal" onChange={onChange} value={formState[currentLang].energy_value_kcal} />
                                    </div>
                                    <div className="col-6">
                                        <label style={{ fontSize: '13px' }} htmlFor="fat">{tc('fat')}</label>
                                        <input type="text" className="form-control mb-2" id="fat" name="fat" onChange={onChange} value={formState[currentLang].fat} />
                                    </div>
                                    <div className="col-6">
                                        <label style={{ fontSize: '13px' }} htmlFor="saturated_fat">{tc('saturated_fat')}</label>
                                        <input type="text" className="form-control mb-2" id="saturated_fat" name="saturated_fat" onChange={onChange} value={formState[currentLang].saturated_fat} />
                                    </div>
                                    <div className="col-6">
                                        <label style={{ fontSize: '13px' }} htmlFor="carbohydrates">{tc('carbohydrates')}</label>
                                        <input type="text" className="form-control mb-2" id="carbohydrates" name="carbohydrates" onChange={onChange} value={formState[currentLang].carbohydrates} />
                                    </div>
                                    <div className="col-6">
                                        <label style={{ fontSize: '13px' }} htmlFor="sugar">{tc('sugars')}</label>
                                        <input type="text" className="form-control mb-2" id="sugar" name="sugar" onChange={onChange} value={formState[currentLang].sugar} />
                                    </div>
                                    <div className="col-6">
                                        <label style={{ fontSize: '13px' }} htmlFor="protein">{tc('protein')}</label>
                                        <input type="text" className="form-control mb-2" id="protein" name="protein" onChange={onChange} value={formState[currentLang].protein} />
                                    </div>
                                    <div className="col-6">
                                        <label style={{ fontSize: '13px' }} htmlFor="salt">{tc('salt')}</label>
                                        <input type="text" className="form-control" id="salt" name="salt" onChange={onChange} value={formState[currentLang].salt} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4" style={{ border: '1px solid #ccc', borderRadius: '10px' }}>
                            <div className="form-group">
                                <label>{tc('content_uppercase')}</label>
                                <input
                                    type="text"
                                    className="form-control mb-2"
                                    id="allergens"
                                    name="allergens"
                                    onChange={onChange}
                                    value={formState[currentLang].allergens}
                                    placeholder={tc('allergens')}
                                    required
                                />
                                <input
                                    type="text"
                                    className="form-control"
                                    id="sulfites"
                                    name="sulfites"
                                    onChange={onChange}
                                    value={formState[currentLang].sulfites}
                                    placeholder={tc('sulfites')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Company details */}
                    <div className="form-group">
                        <label htmlFor="company_name">{tc('manufacturer')}</label>
                        <input type="text" className="form-control" id="company_name" name="company_name" onChange={onChange} value={formState[currentLang].company_name} required />
                    </div>
                    <div className="form-group">
                        <label htmlFor="company_address">{tc('address')}</label>
                        <input type="text" className="form-control" id="company_address" name="company_address" onChange={onChange} value={formState[currentLang].company_address} required />
                    </div>
                    <div className="form-group">
                        <label htmlFor="company_oib">{tc('oib')}</label>
                        <input type="text" className="form-control" id="company_oib" name="company_oib" onChange={onChange} value={formState[currentLang].company_oib} required />
                    </div>
                    <div className="form-group">
                        <label htmlFor="company_phone">{tc('phone')}</label>
                        <input type="text" className="form-control" id="company_phone" name="company_phone" onChange={onChange} value={formState[currentLang].company_phone} required />
                    </div>
                    <div className="form-group">
                        <label htmlFor="company_email">{tc('email')}</label>
                        <input type="text" className="form-control" id="company_email" name="company_email" onChange={onChange} value={formState[currentLang].company_email} required />
                    </div>

                    <button type="button" className="btn btn-primary mt-4" onClick={handleSave}>
                        {t('save')}
                    </button>
                </form>
            )}
        </div>
    );
};

export default MultiLanguageForm;